// Entity slug rules — shared by `grid init` (commands/init.ts) and the
// `grid plug` auto-init path (services/plug-auto-init.ts).
//
// Spec v17 §6.1: lowercase alphanumeric + hyphens, 3-40 chars, no
// leading/trailing hyphen.

export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;
export const SLUG_MIN = 3;
export const SLUG_MAX = 40;

export function isValidSlug(name: string): boolean {
  if (name.length < SLUG_MIN || name.length > SLUG_MAX) return false;
  return SLUG_RE.test(name);
}

/**
 * Best-effort transform of an arbitrary string into a valid slug. Per spec
 * v17 §6.5 the caller only shows a "Try:" line when this returns non-null.
 *
 * Transform: lowercase, replace any run of non-[a-z0-9] with a single hyphen,
 * trim leading/trailing hyphens, strip leading non-letters, truncate to
 * SLUG_MAX. Returns null when the result is shorter than SLUG_MIN or still
 * invalid.
 *
 * The leading-non-letter strip exists because the server's entity-name rule
 * requires the name to begin with a letter, while common directory names lead
 * with a digit (`01-static` → `static`, `2024-blog` → `blog`) or an underscore
 * (`_foo` → `foo`). An all-digit / all-symbol name strips to empty → null, so
 * the caller falls through to its "pass --name <slug>" guidance.
 */
export function suggestSlug(input: string): string | null {
  const lowered = input.toLowerCase();
  const collapsed = lowered.replace(/[^a-z0-9]+/g, '-');
  const trimmed = collapsed.replace(/^-+|-+$/g, '');
  const lettered = trimmed.replace(/^[^a-z]+/, '');
  const truncated = lettered.slice(0, SLUG_MAX);
  if (truncated.length < SLUG_MIN) return null;
  if (!SLUG_RE.test(truncated)) return null;
  return truncated;
}
